/**
 * WorkflowSessionContext — Session slice of the Athena workflow.
 *
 * Owns the backend-session lifecycle state previously held in
 * WorkflowMachineContext.jsx:
 *   - backendSession (threadId, fileId, documentInfo, lastSyncedAt, sessionId)
 *   - backendSessions (the tenant's list)
 *   - isLoadingBackendSessions, backendSessionsError
 *   - tenantId
 *
 * Hierarchical TARA fields (analysis_mode, tara_level, parent_sessions)
 * remain nested in `state.sessionData` inside the god-context for now —
 * Story 4.4 (Results extraction) will rehome them at the same time it
 * splits the rest of `sessionData`.
 *
 * The provider receives `backendServiceRef` as a prop because the ref
 * is created by the outer WorkflowMachineProvider (so the same
 * AthenaBackendService instance is shared with the Machine residue and
 * with revisionWorkflow). The two Session-scoped useEffects from the
 * original god-context (tenant-id sync + backend-service-sync) live here.
 *
 * Hook surface mirrors the existing flat-object pattern: state spread +
 * action-creator functions, NOT { state, dispatch }. Consumers call
 * `session.updateBackendSession({...})`, not `dispatch({type, payload})`.
 *
 * Refs:
 * - Modularity Epic 4 plan §3 / §4.1 (revised after Story 4.1 audit)
 * - docs/ATHENA_WORKFLOW_CONTEXT_SLICES.md §1 (Session row)
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { useCommonAuth } from '../../../contexts/CommonAuthContext';
import logger from '../../../utils/logger';
import { listSessions, createNewSession } from '../../../api/sessions';

const WorkflowSessionContext = createContext(null);

export const SESSION_ACTIONS = {
  SET_BACKEND_SESSION: 'SET_BACKEND_SESSION',
  UPDATE_BACKEND_SESSION: 'UPDATE_BACKEND_SESSION',
  SET_BACKEND_SESSIONS: 'SET_BACKEND_SESSIONS',
  SET_LOADING_BACKEND_SESSIONS: 'SET_LOADING_BACKEND_SESSIONS',
  SET_BACKEND_SESSIONS_ERROR: 'SET_BACKEND_SESSIONS_ERROR',
  SET_TENANT_ID: 'SET_TENANT_ID',
};

export const initialSessionState = {
  backendSession: {
    threadId: null,
    fileId: null,
    documentInfo: null,
    lastSyncedAt: null,
    sessionId: null,
  },
  backendSessions: [],
  isLoadingBackendSessions: false,
  backendSessionsError: null,
  tenantId: null,
};

export function sessionReducer(state, action) {
  switch (action.type) {
    case SESSION_ACTIONS.SET_BACKEND_SESSION:
      return {
        ...state,
        backendSession: action.payload,
      };

    case SESSION_ACTIONS.UPDATE_BACKEND_SESSION:
      return {
        ...state,
        backendSession: {
          ...state.backendSession,
          ...action.payload,
          lastSyncedAt: new Date().toISOString(),
        },
      };

    case SESSION_ACTIONS.SET_BACKEND_SESSIONS:
      return {
        ...state,
        backendSessions: action.payload,
      };

    case SESSION_ACTIONS.SET_LOADING_BACKEND_SESSIONS:
      return {
        ...state,
        isLoadingBackendSessions: action.payload,
      };

    case SESSION_ACTIONS.SET_BACKEND_SESSIONS_ERROR:
      return {
        ...state,
        backendSessionsError: action.payload,
      };

    case SESSION_ACTIONS.SET_TENANT_ID:
      return {
        ...state,
        tenantId: action.payload,
      };

    default:
      return state;
  }
}

export function WorkflowSessionProvider({ children, backendServiceRef }) {
  const [state, dispatch] = useReducer(sessionReducer, initialSessionState);
  const { user } = useCommonAuth();

  // Update tenant ID from authenticated user.
  // Moved verbatim from WorkflowMachineContext.jsx:435-445.
  useEffect(() => {
    if (user && user.tenant_id && state.tenantId !== user.tenant_id) {
      logger.debug('🔐 Setting tenant ID from authenticated user:', user.tenant_id);
      dispatch({
        type: SESSION_ACTIONS.SET_TENANT_ID,
        payload: user.tenant_id,
      });
      if (backendServiceRef?.current) {
        backendServiceRef.current.tenantId = user.tenant_id;
      }
    }
  }, [user, state.tenantId, backendServiceRef]);

  // Ensure backend service is always in sync with backend session state.
  // Moved verbatim from WorkflowMachineContext.jsx:456-487.
  useEffect(() => {
    if (state.backendSession && backendServiceRef?.current) {
      logger.debug('🔄 Syncing backend service with backend session:', state.backendSession);

      if (state.backendSession.threadId && backendServiceRef.current.threadId !== state.backendSession.threadId) {
        backendServiceRef.current.threadId = state.backendSession.threadId;
        logger.debug('Updated backend service threadId:', state.backendSession.threadId);
      }

      if (state.backendSession.sessionId && backendServiceRef.current.sessionId !== state.backendSession.sessionId) {
        backendServiceRef.current.sessionId = state.backendSession.sessionId;
        logger.debug('Updated backend service sessionId:', state.backendSession.sessionId);
      }

      if (state.backendSession.tenantId && backendServiceRef.current.tenantId !== state.backendSession.tenantId) {
        backendServiceRef.current.tenantId = state.backendSession.tenantId;
        logger.debug('Updated backend service tenantId:', state.backendSession.tenantId);
      }

      if (state.backendSession.fileId && backendServiceRef.current.fileId !== state.backendSession.fileId) {
        backendServiceRef.current.fileId = state.backendSession.fileId;
        logger.debug('Updated backend service fileId:', state.backendSession.fileId);
      }

      // Also sync with global tenant ID if backend session doesn't have it
      if (!state.backendSession.tenantId && state.tenantId && backendServiceRef.current.tenantId !== state.tenantId) {
        backendServiceRef.current.tenantId = state.tenantId;
        logger.debug('Updated backend service tenantId from global state:', state.tenantId);
      }
    }
  }, [state.backendSession, state.tenantId, backendServiceRef]);

  // Action-creators (preserve byte-equivalent behaviour with the originals
  // in WorkflowMachineContext.jsx:683-744).

  const updateBackendSession = useCallback((updates) => {
    dispatch({ type: SESSION_ACTIONS.UPDATE_BACKEND_SESSION, payload: updates });

    if (backendServiceRef?.current) {
      if (updates.threadId !== undefined) {
        backendServiceRef.current.threadId = updates.threadId;
      }
      if (updates.fileId !== undefined) {
        backendServiceRef.current.fileId = updates.fileId;
      }
      if (updates.sessionId !== undefined) {
        backendServiceRef.current.sessionId = updates.sessionId;
      }
      if (updates.tenantId !== undefined) {
        backendServiceRef.current.tenantId = updates.tenantId;
      }
    }
  }, [backendServiceRef]);

  /**
   * Wholesale replace backendSession (no merge, no lastSyncedAt stamp).
   * Used by session-persistence restore in the god-context residue —
   * the saved snapshot already contains a complete backendSession object
   * that should overwrite verbatim. Preserves byte-equivalence with the
   * pre-split `dispatch({ type: 'SET_BACKEND_SESSION', payload })`.
   */
  const setBackendSession = useCallback((payload) => {
    dispatch({ type: SESSION_ACTIONS.SET_BACKEND_SESSION, payload });
  }, []);

  const fetchBackendSessions = useCallback(async () => {
    // Source tenantId synchronously from useCommonAuth's user object
    // rather than relying on state.tenantId, which is set by an
    // async useEffect that may not have fired yet on first cold mount
    // (provider effects run AFTER child consumer effects, and
    // CommonAuth's user is loaded asynchronously).
    const effectiveTenantId = state.tenantId || user?.tenant_id || null;
    if (!effectiveTenantId) {
      logger.warn('⚠️ fetchBackendSessions called before tenantId available — skipping');
      return [];
    }

    dispatch({ type: SESSION_ACTIONS.SET_LOADING_BACKEND_SESSIONS, payload: true });
    dispatch({ type: SESSION_ACTIONS.SET_BACKEND_SESSIONS_ERROR, payload: null });

    try {
      logger.debug('📡 Fetching sessions from backend API for tenant:', effectiveTenantId);
      // Story 6.1: legacy getTenantSessions(tid) ignored its arg — server
      // scopes by JWT. listSessions() hits the same /api/v1/sessions.
      const result = await listSessions();

      const sessions = result.sessions || [];
      logger.debug(`✅ Fetched ${sessions.length} session(s) from backend API`);

      dispatch({ type: SESSION_ACTIONS.SET_BACKEND_SESSIONS, payload: sessions });

      return sessions;
    } catch (error) {
      logger.error('❌ Failed to fetch backend sessions:', error);
      dispatch({ type: SESSION_ACTIONS.SET_BACKEND_SESSIONS_ERROR, payload: error.message });
      return [];
    } finally {
      dispatch({ type: SESSION_ACTIONS.SET_LOADING_BACKEND_SESSIONS, payload: false });
    }
  }, [state.tenantId, backendServiceRef, user]);

  const createBackendSession = useCallback(async (analysisName, options = {}) => {
    try {
      // Story 6.1: stateless mirror in api/sessions.js. Same signature.
      const result = await createNewSession(state.tenantId, analysisName, options);

      updateBackendSession({
        sessionId: result.session_id,
        tenantId: result.tenant_id,
      });

      await fetchBackendSessions();

      return result;
    } catch (error) {
      logger.error('Failed to create backend session:', error);
      throw error;
    }
  }, [state.tenantId, updateBackendSession, fetchBackendSessions, backendServiceRef]);

  const contextValue = useMemo(() => ({
    // State
    ...state,

    // Action-creators
    updateBackendSession,
    setBackendSession,
    fetchBackendSessions,
    createBackendSession,
  }), [
    state,
    updateBackendSession,
    setBackendSession,
    fetchBackendSessions,
    createBackendSession,
  ]);

  return (
    <WorkflowSessionContext.Provider value={contextValue}>
      {children}
    </WorkflowSessionContext.Provider>
  );
}

export function useWorkflowSession() {
  const context = useContext(WorkflowSessionContext);

  if (!context) {
    throw new Error('useWorkflowSession must be used within a WorkflowSessionProvider');
  }

  return context;
}
